import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import ProjectInfoBanner from '../components/detail/ProjectInfoBanner';
import ProjectActionButtons from '../components/detail/ProjectActionButtons';
import SitePlanSectionDetail from '../components/detail/SitePlanSectionDetail';
import TowerViewSection from '../components/detail/TowerViewSection';
import AvailableUnitsSection from '../components/detail/AvailableUnitsSection';
import UnitActionsMenu, { UnitMenuAnchor } from '../components/detail/unit/UnitActionsMenu';
import FloorPlanModal from '../components/detail/unit/FloorPlanModal';
import {
  buildPublicUnitSyntheticKey,
  type PublicUnitForMenu,
} from '../components/detail/unit/types';
import { useFloorPlanCompare } from '../hooks/useFloorPlanCompare';
import {
  fetchPublicProjectUnitTypes,
  revealSection,
  type PublicProjectUnitType,
  type RevealedTowerViewUnitPrices,
} from '../services/superPickerPublicApi';
import {
  useRevealedPrices,
  useRevealedSection,
} from '../services/revealedPrices';
import { getPublicShareUuid } from '../services/shareContext';
import { useTokenSpend } from '../components/TokenSpendDialog';
import { useProjectDetail } from '../hooks/useProjectDetail';
import {
  logPublicFloorPlanComparisonOpened,
  logPublicFloorPlanOpened,
  logPublicProjectViewed,
  logPublicSitePlanOpened,
} from '../services/analytics';
import {
  ProjectDetailInfo,
  SitePlanBlock,
  SitePlanBounds,
  SitePlanItem,
  StackUnitInfo,
  UpcomingProject,
} from '../types';

interface ProjectDetailPageProps {
  /** Project id from the list. */
  projectId: string;
  /** Optional list-row context so the mock fallback can show real meta. */
  fromListItem?: SitePlanItem | UpcomingProject | null;
  onBack: () => void;
  /**
   * Hide the "BACK TO LIST" button without removing it from the tree. Used
   * by share-link recipients pinned to a single project — they have no list
   * to return to. We `display: none` rather than unmounting so the button
   * code stays in place for future flows.
   */
  hideBackButton?: boolean;
}

const isUpcoming = (
  item: SitePlanItem | UpcomingProject | null | undefined,
): item is UpcomingProject => Boolean(item && 'name' in item);

export const ProjectDetailPage: React.FC<ProjectDetailPageProps> = ({
  projectId,
  fromListItem,
  onBack,
  hideBackButton = false,
}) => {
  const live = useProjectDetail(projectId);

  // Site-plan filters lifted to the page so the same `(bedroomFilter,
  // unitTypeFilter)` pair drives both `SitePlanSectionDetail` (the
  // dot blink/hide logic) and `TowerViewSection` (the All Blocks grid
  // visibility). Mirrors the agent-side `sitePlanBedroomFilter` /
  // `sitePlanUnitTypeFilter` plumbing in
  // `frontend/src/super-picker/SuperPickerApp.tsx`.
  const [sitePlanBedroomFilter, setSitePlanBedroomFilter] = useState<number[]>([]);
  const [sitePlanUnitTypeFilter, setSitePlanUnitTypeFilter] = useState<string[]>([]);
  // Stack selection lifted from the All Blocks Tower View so the same
  // value drives both the tower grid's Stack dropdown and the site-plan
  // dots (which filter + pulse for the selected stacks). Mirrors the
  // agent's `sitePlanStackFilter` plumbing in `SuperPickerApp.tsx`.
  const [sitePlanStackFilter, setSitePlanStackFilter] = useState<string[]>([]);

  // Expanded set of unit-type codes that map to the selected
  // descriptions in `sitePlanUnitTypeFilter`. Derived from the
  // catalogue below (`projectUnitTypes`). Mirrors the agent's
  // `sitePlanUnitTypeFilterExpandedUnitTypes` (defined in
  // `frontend/src/super-picker/SuperPickerApp.tsx`) — selecting
  // "3 Bedroom Premier" expands to every code grouped under that
  // description (e.g. `C5`, `C6`) so units whose `unitType` code
  // appears in the tower-view payload (with a missing description)
  // still match the filter. Populated below once the catalogue
  // arrives.
  // Project unit-type catalogue — populated by the public counterpart to
  // the agent's `getUnitTypes` API. We use it to label the Unit Type
  // filter dropdown so it surfaces every unit type the developer
  // defined (even ones with no live units), matching the agent's
  // `sitePlanUnitTypes`-driven dropdown in `TopViewingSitePlanDetail`.
  const [projectUnitTypes, setProjectUnitTypes] = useState<PublicProjectUnitType[]>([]);
  useEffect(() => {
    if (!projectId) {
      setProjectUnitTypes([]);
      return;
    }
    let cancelled = false;
    fetchPublicProjectUnitTypes(projectId)
      .then((payload) => {
        if (!cancelled) setProjectUnitTypes(payload.unitTypes);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[ProjectDetailPage] fetch unit types failed:', err);
          setProjectUnitTypes([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Build the expanded code-set from the active descriptions + catalogue.
  // Empty set when no description filter is active (the consumers treat
  // it as a no-op in that case). Trimming + Set used so the lookup at
  // match time is O(1). Agent parity: `SuperPickerApp.tsx ::
  // sitePlanUnitTypeFilterExpandedUnitTypes`.
  const sitePlanUnitTypeFilterCodes = useMemo(() => {
    const set = new Set<string>();
    if (sitePlanUnitTypeFilter.length === 0 || projectUnitTypes.length === 0) {
      return set;
    }
    const selected = new Set(sitePlanUnitTypeFilter);
    for (const row of projectUnitTypes) {
      if (!selected.has(row.description)) continue;
      // `unitTypes[]` carries every code grouped under this description;
      // we also defensively add the row's representative `unitType`.
      for (const code of row.unitTypes) {
        const c = (code || '').trim();
        if (c) set.add(c);
      }
      if (row.unitType) {
        const c = row.unitType.trim();
        if (c) set.add(c);
      }
    }
    return set;
  }, [sitePlanUnitTypeFilter, projectUnitTypes]);

  // ----- Unit-actions menu + floor-plan / compare modals -----
  // Mirrors the agent's `sectionUnitMenuAnchor` state pattern from
  // `TopViewingSitePlanDetail.tsx`. The menu is anchored at the click
  // viewport position; the floor-plan modal opens a single unit; the
  // compare cart is a 3-slot in-memory list driven by `useFloorPlanCompare`.
  const [unitMenuAnchor, setUnitMenuAnchor] = useState<UnitMenuAnchor | null>(null);
  /**
   * `true` when the menu was opened from a context that has no floor-plan
   * or compare affordance (e.g. a TowerView grid cell). The menu then
   * surfaces only the Reveal Prices item.
   */
  const [unitMenuRevealOnly, setUnitMenuRevealOnly] = useState(false);
  const [floorPlanUnit, setFloorPlanUnit] = useState<PublicUnitForMenu | null>(null);
  const compare = useFloorPlanCompare();
  const isUnitInCompareCart = useCallback(
    (unitKey: string) => compare.isInCart(unitKey, projectId),
    [compare, projectId],
  );

  const handleUnitClick = useCallback(
    (unit: PublicUnitForMenu, mouseX: number, mouseY: number) => {
      setUnitMenuRevealOnly(false);
      setUnitMenuAnchor({ unit, mouseX, mouseY });
    },
    [],
  );
  /** Opens the menu in reveal-only mode (no floor-plan / compare items). */
  const handleCellClickRevealOnly = useCallback(
    (unit: PublicUnitForMenu, mouseX: number, mouseY: number) => {
      setUnitMenuRevealOnly(true);
      setUnitMenuAnchor({ unit, mouseX, mouseY });
    },
    [],
  );
  const handleViewFloorPlan = useCallback(
    (unit: PublicUnitForMenu) => {
      // Read the project name lazily from the live-details hook (the
      // memoised `project` object isn't in scope this early in the
      // component body). Falling back to `null` keeps the analytics
      // helper happy when details haven't resolved yet.
      logPublicFloorPlanOpened({
        projectName: live.details?.name ?? null,
        projectId,
        unitNumber: unit.unitNumber ?? null,
        source: 'project_details_page',
      });
      setFloorPlanUnit(unit);
    },
    [live.details?.name, projectId],
  );
  const handleToggleCompare = useCallback(
    (unit: PublicUnitForMenu) => {
      compare.toggle({ ...unit, projectId: unit.projectId ?? projectId });
    },
    [compare, projectId],
  );

  // ----- Per-row reveal-prices flow -------------------------------------
  // The unit-actions menu (`UnitActionsMenu`) renders a "Reveal prices
  // (1 token)" item which calls `handleRevealPricesRequest(unit)` below.
  // We stash the requested unit in `revealPricesUnit` and let the
  // `useTokenSpend` hook drive the confirmation dialog. On confirm the
  // dialog POSTs to `.../share/:uuid/reveal` with `{ section: 'towerView',
  // projectId, unitKey }`, decrements the share token, and writes the
  // unmasked single-unit map into `RevealedPricesContext` so the rows
  // pick up the real numbers on next render.
  const [revealPricesUnit, setRevealPricesUnit] = useState<PublicUnitForMenu | null>(null);
  const revealedUnits = useRevealedSection('towerView', projectId);
  const { setRevealed } = useRevealedPrices();

  const revealPrices = useTokenSpend<{
    units: Record<string, RevealedTowerViewUnitPrices>;
  }>({
    title: 'Reveal prices',
    actionLabel: 'Reveal',
    description: revealPricesUnit ? (
      <>
        Spend <strong>1 token</strong> to unmask the prices for unit{' '}
        <strong>{revealPricesUnit.unitNumber}</strong>?
      </>
    ) : undefined,
    spend: async () => {
      const uuid = getPublicShareUuid();
      if (!uuid) throw new Error('Missing share context.');
      if (!projectId) throw new Error('Missing projectId.');
      if (!revealPricesUnit) throw new Error('No unit selected.');
      const r = await revealSection(uuid, {
        section: 'towerView',
        projectId,
        unitKey: revealPricesUnit.unitKey,
      });
      if (r.section !== 'towerView') {
        throw new Error('Unexpected reveal response.');
      }
      return {
        remainingTokens: r.remainingTokens,
        maxTokens: r.maxTokens,
        data: { units: r.units },
      };
    },
    onConfirmed: ({ data }) => {
      if (!data) return;
      // Merge into the existing per-project unit map so previously
      // revealed units stay revealed.
      const existing = revealedUnits ?? {};
      setRevealed('towerView', projectId, { ...existing, ...data.units });
      setRevealPricesUnit(null);
    },
  });

  const handleRevealPricesRequest = useCallback((unit: PublicUnitForMenu) => {
    setRevealPricesUnit(unit);
    revealPrices.request();
  }, [revealPrices]);

  // Hide the Reveal Prices menu item for agent EXTERNAL sessions (no uuid)
  // and pass `isPricesRevealed` so the menu can swap to a "Prices revealed"
  // disabled indicator.
  const canRevealPrices = Boolean(getPublicShareUuid());
  const isUnitRevealed = useCallback(
    (unit: PublicUnitForMenu | null | undefined): boolean => {
      if (!unit || !revealedUnits) return false;
      if (unit.unitKey && revealedUnits[unit.unitKey]) return true;
      const synthetic = buildPublicUnitSyntheticKey(unit.blockNumber, unit.unitNumber);
      return Boolean(revealedUnits[synthetic]);
    },
    [revealedUnits],
  );

  // Project info banner data — pulled from the public details endpoint with
  // light fallbacks to the list-row context the user clicked through from.
  const project = useMemo<ProjectDetailInfo>(() => {
    const listName = isUpcoming(fromListItem)
      ? fromListItem.name
      : fromListItem?.projectName;
    const d = live.details;
    return {
      id: projectId,
      name: d?.name ?? listName ?? 'Project',
      topYear: d?.completedDate ?? fromListItem?.topYear ?? '',
      district: d?.district ?? fromListItem?.district ?? '',
      region: d?.area ?? fromListItem?.region ?? '',
      totalUnits: d?.totalUnits ?? 0,
      developer: d?.developer ?? undefined,
      tenure: d?.tenure ?? undefined,
      completedDate: d?.completedDate ?? undefined,
      area: d?.area ?? undefined,
      address: d?.address ?? undefined,
      mapLatitude: d?.mapLatitude ?? undefined,
      mapLongitude: d?.mapLongitude ?? undefined,
      featuredImage: d?.featuredImage ?? null,
    };
  }, [projectId, fromListItem, live.details]);

  // Site plan coordinates from the public site-plan endpoint.
  const sitePlanBlocks = useMemo<SitePlanBlock[]>(() => {
    if (!live.sitePlan) return [];
    return live.sitePlan.blocks
      .filter((b) => b.stack)
      .map((b) => ({
        blockNumber: b.blockNumber ?? '',
        stack: String(b.stack),
        facing: b.facing ?? undefined,
        x: b.x,
        y: b.y,
        width: b.width,
        height: b.height,
      }));
  }, [live.sitePlan]);

  const sitePlanBounds: SitePlanBounds | null = live.sitePlan?.bounds ?? null;

  const sitePlanImageUrl: string | null =
    live.sitePlan?.imageUrl ?? null;

  // Per-stack filter metadata for the Site Plan section — first occurrence
  // of bedroom/unit-type per stack from the live tower-view feed, plus a
  // sticky `isPenthouse` flag that flips true if ANY unit in the stack
  // reads as a penthouse. Mirrors the agent-side `isPenthouseUnit` /
  // `sitePlanBedroomFilterOptions` logic in `TopViewingSitePlanDetail.tsx`
  // so the public Bedroom filter can offer a Penthouse option.
  const stackInfos = useMemo<StackUnitInfo[]>(() => {
    if (!live.towerView) return [];
    const isPenthouseText = (value: unknown): boolean => {
      const raw = String(value ?? '').trim().toLowerCase();
      if (!raw) return false;
      return (
        raw.includes('penthouse') ||
        raw.includes('penthhouse') ||
        /\bph\b/.test(raw) ||
        raw.endsWith('ph')
      );
    };
    const map = new Map<string, StackUnitInfo>();
    for (const u of live.towerView.units) {
      const m = (u.unitNumber || '').replace(/^#/, '').match(/^\d+\s*-\s*(\d+)$/);
      if (!m) continue;
      const stack = m[1];
      const unitIsPenthouse =
        isPenthouseText(u.unitTypeDescription) || isPenthouseText(u.unitType);
      const existing = map.get(stack);
      if (existing) {
        // Promote the stack-level PH flag if any later unit in the stack
        // is a penthouse — the first unit we saw may have been a regular
        // bedroom type (e.g. low-floor stack of mixed types).
        if (unitIsPenthouse && !existing.isPenthouse) {
          existing.isPenthouse = true;
        }
        continue;
      }
      map.set(stack, {
        stack,
        bedroom: Number.isFinite(u.bedroom) ? u.bedroom : 0,
        unitType: u.unitTypeDescription || u.unitType || '',
        // Original code (e.g. `C5`) kept alongside the display label so
        // the Unit Type filter can match via the agent-style expanded
        // code-set even when the upstream payload only carries a code.
        unitTypeCode: (u.unitType || '').trim() || undefined,
        isPenthouse: unitIsPenthouse,
      });
    }
    return Array.from(map.values());
  }, [live.towerView]);

  // Fire `project_viewed` once per project, and again whenever the live
  // project name first arrives (so the event payload reflects the real
  // name rather than the placeholder we used before details loaded).
  const trackedProjectKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId) return;
    const key = `${projectId}::${project.name || ''}`;
    if (trackedProjectKeyRef.current === key) return;
    trackedProjectKeyRef.current = key;
    logPublicProjectViewed(project.name || null, 'project_details_page', projectId);
  }, [projectId, project.name]);

  // `site_plan_opened` parity with the agent — fire once when the live
  // site-plan payload first arrives for this project. Mirrors the
  // agent's `logSitePlanOpened` call in `handleOpenSitePlanForProject`.
  const trackedSitePlanProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId || !live.sitePlan) return;
    if (trackedSitePlanProjectIdRef.current === projectId) return;
    trackedSitePlanProjectIdRef.current = projectId;
    logPublicSitePlanOpened(project.name || null, 'project_site_plan', projectId);
  }, [projectId, live.sitePlan, project.name]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicHeader />
      <Box
        data-public-export-root
        sx={{ width: '100%', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              borderColor: 'rgba(var(--color-text-rgb), 0.3)',
              color: 'var(--color-text-strong)',
              fontWeight: 600,
              // Hidden (but kept in tree) for share-link recipients pinned to
              // this single project.
              display: hideBackButton ? 'none' : undefined,
            }}
          >
            BACK TO LIST
          </Button>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              sx={{
                border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                color: 'var(--color-text-strong)',
                borderRadius: 1,
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <ProjectInfoBanner project={project} />
        <ProjectActionButtons />
        <SitePlanSectionDetail
          projectName={project.name}
          projectId={projectId}
          onUnitClick={handleUnitClick}
          imageUrl={sitePlanImageUrl}
          blocks={sitePlanBlocks}
          bounds={sitePlanBounds}
          stackInfos={stackInfos}
          stackSoldPercentages={live.stackSoldPercentages}
          mapLatitude={project.mapLatitude}
          mapLongitude={project.mapLongitude}
          bedroomFilter={sitePlanBedroomFilter}
          onBedroomFilterChange={setSitePlanBedroomFilter}
          unitTypeFilter={sitePlanUnitTypeFilter}
          onUnitTypeFilterChange={setSitePlanUnitTypeFilter}
          projectUnitTypes={projectUnitTypes}
          unitTypeFilterCodes={sitePlanUnitTypeFilterCodes}
          stackFilter={sitePlanStackFilter}
        />
        <TowerViewSection
          units={live.towerView?.units ?? []}
          projectId={projectId}
          // Use the full unit-actions menu (Floor Plan + Add to Compare +
          // Reveal Prices). TowerViewSection now threads `floorplan` /
          // `bathroom` / `projectName` through to the `PublicUnitForMenu`
          // so all three actions resolve to real assets/state. The
          // reveal-only callback (`handleCellClickRevealOnly`) stays
          // wired up for any future grid that legitimately has nothing
          // but pricing to act on.
          onCellClick={handleUnitClick}
          isInCompareCart={isUnitInCompareCart}
          bedroomFilter={sitePlanBedroomFilter}
          unitTypeFilter={sitePlanUnitTypeFilter}
          unitTypeFilterCodes={sitePlanUnitTypeFilterCodes}
          stackFilter={sitePlanStackFilter}
          onStackFilterChange={setSitePlanStackFilter}
        />
        <AvailableUnitsSection
          projectId={projectId}
          liveAvailableUnits={live.towerView?.units ?? null}
          bestByPrice={live.bestByPrice}
          bestByPsf={live.bestByPsf}
          sitePlanImageUrl={sitePlanImageUrl}
          sitePlanBlocks={sitePlanBlocks}
          sitePlanBounds={sitePlanBounds}
          stackInfos={stackInfos}
          stackSoldPercentages={live.stackSoldPercentages}
          projectName={project.name}
          mapLatitude={project.mapLatitude}
          mapLongitude={project.mapLongitude}
          onUnitClick={handleUnitClick}
          isInCompareCart={isUnitInCompareCart}
          // Site-plan filters drive the Available Units list (mirrors
          // the agent-side `topViewingFilteredAvailableUnits`). The
          // expanded code-set rescues PREMIUM rows that arrive with
          // only a unit-type code populated. Best PX / Best PSF tabs
          // are intentionally NOT filtered here — they render the
          // backend payload as-is, matching the agent
          // (`TopViewingSitePlanDetail.tsx :: renderTopViewingBestBedroomContent`).
          bedroomFilter={sitePlanBedroomFilter}
          unitTypeFilter={sitePlanUnitTypeFilter}
          unitTypeFilterCodes={sitePlanUnitTypeFilterCodes}
          stackFilter={sitePlanStackFilter}
        />

        <Footer />
      </Box>

      {/* Public unit-actions menu, floor-plan viewer + compare cart. */}
      <UnitActionsMenu
        anchor={unitMenuAnchor}
        onClose={() => setUnitMenuAnchor(null)}
        onViewFloorPlan={unitMenuRevealOnly ? undefined : handleViewFloorPlan}
        onToggleCompare={unitMenuRevealOnly ? undefined : handleToggleCompare}
        onRevealPrices={canRevealPrices ? handleRevealPricesRequest : undefined}
        isPricesRevealed={isUnitRevealed(unitMenuAnchor?.unit)}
        isInCart={isUnitInCompareCart}
        cartSize={compare.cart.length}
        capacity={compare.capacity}
      />
      {/* Token-spend confirmation dialog for the per-row Reveal Prices flow. */}
      {revealPrices.dialog}
      <FloorPlanModal
        open={Boolean(floorPlanUnit)}
        unit={floorPlanUnit}
        onClose={() => setFloorPlanUnit(null)}
        isInCompare={
          floorPlanUnit ? compare.isInCart(floorPlanUnit.unitKey, projectId) : false
        }
        compareCount={compare.cart.length}
        compareCapacity={compare.capacity}
        onToggleCompare={handleToggleCompare}
        onOpenCompare={() => {
          logPublicFloorPlanComparisonOpened({
            projectName: project.name || null,
            projectId,
            source: 'floor_plan_modal',
          });
          compare.openCompare();
        }}
      />
    </Box>
  );
};

export default ProjectDetailPage;
